import {
  AdminCreateOnboardingTemplate,
  AdminFetchOnboardingTemplate,
  AdminRenameOnboardingTemplate,
  AdminSaveOnboardingTemplate,
  FlowEditorApplyOp,
  FlowEditorInitCreateDraft,
  FlowEditorSetDraft,
} from 'src/constants/actionTypes'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'
import { Action } from 'src/utils/reduxUtils'
import { applyDocumentOp } from 'src/utils/flowEditorDocument/applyDocumentOp'
import { emptyOnboardingTemplate } from 'src/utils/flowEditorDocument/createDraft'
import { templateToDocument } from 'src/utils/flowEditorDocument/fromApi'
import type { FlowEditorDocumentOp } from 'src/utils/flowEditorDocument/types'
import { OnboardingFlowEditorState } from './types'

const getInitialState = (): OnboardingFlowEditorState => ({
  template: null,
  savedSnapshot: null,
  draft: null,
  loading: false,
  saving: false,
  error: null,
})

export const onboardingFlowEditorReducer = (
  state: OnboardingFlowEditorState = getInitialState(),
  action: Action<any>,
): OnboardingFlowEditorState => {
  switch (action.type) {
    // ── Fetch ────────────────────────────────────────────────────────────
    case AdminFetchOnboardingTemplate.type.REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      }
    case AdminFetchOnboardingTemplate.type.SUCCESS: {
      const template = action.payload as OnboardingTemplate
      const snapshot = JSON.parse(JSON.stringify(template)) as OnboardingTemplate

      return {
        ...state,
        template,
        savedSnapshot: snapshot,
        draft: templateToDocument(snapshot),
        loading: false,
        error: null,
      }
    }
    // ── New-template mode ────────────────────────────────────────────────
    // Runs unconditionally on entering create mode, so a previously loaded template cannot flash
    // through the editor before the scaffold lands.
    case FlowEditorInitCreateDraft.type: {
      const { partnerId } = action.payload as { partnerId: string }
      const scaffold = emptyOnboardingTemplate(partnerId)

      return {
        ...state,
        template: scaffold,
        savedSnapshot: emptyOnboardingTemplate(partnerId),
        draft: templateToDocument(scaffold),
        loading: false,
        saving: false,
        error: null,
      }
    }
    case FlowEditorSetDraft.type:
      return {
        ...state,
        draft: action.payload,
      }
    case FlowEditorApplyOp.type: {
      if (!state.draft) {
        return state
      }

      return {
        ...state,
        draft: applyDocumentOp(state.draft, action.payload as FlowEditorDocumentOp),
      }
    }
    case AdminFetchOnboardingTemplate.type.FAILED:
      return {
        ...state,
        loading: false,
        error: 'Failed to fetch template',
      }

    // ── Save ─────────────────────────────────────────────────────────────
    case AdminSaveOnboardingTemplate.type.REQUEST:
      return {
        ...state,
        saving: true,
        error: null,
      }
    case AdminSaveOnboardingTemplate.type.SUCCESS: {
      const saved = action.payload as OnboardingTemplate
      const snapshot = JSON.parse(JSON.stringify(saved)) as OnboardingTemplate

      return {
        ...state,
        template: saved,
        savedSnapshot: snapshot,
        draft: templateToDocument(snapshot),
        saving: false,
      }
    }
    // The rename endpoint returns a bare CollectDataTemplateEntity — no nested configs — so only
    // the name is taken from it. Feeding the whole response in would wipe the step groups.
    case AdminRenameOnboardingTemplate.type.SUCCESS: {
      const renamed = action.payload as { id?: string; name?: string } | undefined
      const name = renamed?.name

      if (!name || !state.template || !state.savedSnapshot || !state.draft) {
        return state
      }

      // The saga stamps the requested template id onto the response. A rename that resolves
      // after a different template has loaded belongs to neither the loaded template nor its
      // draft, so it is discarded rather than applied to the wrong document.
      if (renamed?.id !== state.template.id) {
        return state
      }

      return {
        ...state,
        template: { ...state.template, name },
        savedSnapshot: { ...state.savedSnapshot, name },
        draft: { ...state.draft, name },
      }
    }
    case AdminSaveOnboardingTemplate.type.FAILED:
      return {
        ...state,
        saving: false,
        error: 'Failed to save template',
      }

    // ── Create ───────────────────────────────────────────────────────────
    case AdminCreateOnboardingTemplate.type.REQUEST:
      return {
        ...state,
        saving: true,
        error: null,
      }
    // The create response is the full template, so the editor continues against it in normal edit
    // mode — an immediate second save diffs against a persisted baseline and issues an update.
    case AdminCreateOnboardingTemplate.type.SUCCESS: {
      const created = action.payload as OnboardingTemplate
      const snapshot = JSON.parse(JSON.stringify(created)) as OnboardingTemplate

      return {
        ...state,
        template: created,
        savedSnapshot: snapshot,
        draft: templateToDocument(snapshot),
        saving: false,
      }
    }
    // `draft` is deliberately untouched — the user's unsaved work survives a failed create.
    case AdminCreateOnboardingTemplate.type.FAILED:
      return {
        ...state,
        saving: false,
        error: 'Failed to create template',
      }

    default:
      return state
  }
}
